import React, { useEffect, useState } from 'react';
import { Wrapper } from '../style';
import { Button, Checkbox, ConfigProvider, Dropdown, Menu, message, Modal, Pagination, Table, Tag, Tooltip, Select } from 'antd';
import { fakeProImage } from '../../../../config/fakeData';
import Type from './type';
import Category from './category';
import { Link } from 'react-router-dom';
import { ConvertParams, defaultPageSize } from '../../../../config';
import { MakeApiCall } from '../../../../apis';
import moment from 'moment';
import { useAlerts } from '../../../../contexts/AlertContext';

const READ_ROW_CLASS = 'alert-read-row';

function AllAlerts({
                       userData,
                       selectedAsins = [],
                       selectedCategory = null,
                       selectedSeverity = null,
                       selectedDateRange = [],
                       onCategoryChange,
                   }) {

    const [activeTab, setActiveTab] = useState('category');

    const [totalPage, setTotalPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);

    // --- Selected row IDs for bulk action ---
    const [selectedIds, setSelectedIds] = useState([]);
    const [markingRead, setMarkingRead] = useState(false);
    const isMounted = React.useRef(true);

    const [analyticsData, setAnalyticsData] = useState({
        total_alerts: 0,
        unread_count: 0,
        alerts_by_categories: [],
        alerts_by_type: [],
    });
    const [analyticsLoading, setAnalyticsLoading] = useState(true);

    const [filter, setFilter] = useState({
        page: 1,
        'per-page': defaultPageSize,
        search_text: '',
        alert_name: '',
        is_read: '0',
        asin: '',
        category: '',
        severity: '',
        start_date: '',
        end_date: '',
    });

    const { setUnreadCount } = useAlerts();

    const getProductList = async (currentFilter) => {
        setLoading(true);
        const response = await MakeApiCall(
            `get-alert-notifications${ConvertParams(currentFilter)}`,
            'get',
            null,
            true,
            {},
            userData?.token
        );

        if (response?.status) {
            if (!isMounted.current) return;
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            const records = response?.data?.records || [];
            const recordsWithReadStatus = records.map(record => ({
                ...record,
                is_read: record.is_read === 1,
                key: record.id,
            }));
            setList(recordsWithReadStatus);
            setSelectedIds([]);
            setLoading(false);
        } else {
            if (!isMounted.current) return;
            message.warning(response?.message);
            setLoading(false);
            setList([]);
        }
    };

    // Accepts the same filter object so analytics always reflects current filter state
    const fetchAnalyticsData = async (currentFilter = {}) => {
        if (!isMounted.current) return;
        setAnalyticsLoading(true);

        // Build analytics params from the same filter fields used by the list endpoint
        const analyticsParams = {
            ...(currentFilter.asin       ? { asin: currentFilter.asin }             : {}),
            ...(currentFilter.category   ? { category: currentFilter.category }     : {}),
            ...(currentFilter.severity   ? { severity: currentFilter.severity }     : {}),
            ...(currentFilter.start_date ? { start_date: currentFilter.start_date } : {}),
            ...(currentFilter.end_date   ? { end_date: currentFilter.end_date }     : {}),
            ...(currentFilter.alert_name ? { alert_name: currentFilter.alert_name } : {}),
            ...(currentFilter.is_read !== null && currentFilter.is_read !== undefined
                ? { is_read: currentFilter.is_read }
                : {}),
        };

        try {
            const response = await MakeApiCall(
                `get-alert-notification-analytics${ConvertParams(analyticsParams)}`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (!isMounted.current) return;

            if (response?.status) {
                const data = {
                    total_alerts: response.data.total_alerts || 0,
                    unread_count: response.data.unread_count || 0,
                    alerts_by_categories: Array.isArray(response.data.alerts_by_categories)
                        ? response.data.alerts_by_categories
                        : [],
                    alerts_by_type: Array.isArray(response.data.alerts_by_type)
                        ? response.data.alerts_by_type
                        : [],
                };
                setAnalyticsData(data);
                setUnreadCount(data.unread_count);
            } else {
                message.warning(response?.message || 'Failed to load alert analytics.');
                setAnalyticsData({ total_alerts: 0, unread_count: 0, alerts_by_categories: [], alerts_by_type: [] });
                setUnreadCount(0);
            }
        } catch (error) {
            if (!isMounted.current) return;
            console.error('Error fetching alert analytics:', error);
            message.error('Error fetching alert analytics.');
            setAnalyticsData({ total_alerts: 0, unread_count: 0, alerts_by_categories: [], alerts_by_type: [] });
            setUnreadCount(0);
        } finally {
            if (isMounted.current) setAnalyticsLoading(false);
        }
    };

    // Track mounted state to avoid state updates after unmount
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Sync filters from parent dropdowns (asin, category, severity, date range)
    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            asin: Array.isArray(selectedAsins) && selectedAsins.length ? selectedAsins.join(',') : '',
            category: selectedCategory || '',
            severity: selectedSeverity || '',
            start_date: Array.isArray(selectedDateRange) && selectedDateRange[0]
                ? selectedDateRange[0].format('YYYY-MM-DD')
                : '',
            end_date: Array.isArray(selectedDateRange) && selectedDateRange[1]
                ? selectedDateRange[1].format('YYYY-MM-DD')
                : '',
            page: 1,
        }));
    }, [selectedAsins, selectedCategory, selectedSeverity, selectedDateRange]);

    // Fetch both list and analytics whenever filter changes
    useEffect(() => {
        if (userData?.token) {
            getProductList(filter);
            fetchAnalyticsData(filter);
        }
    }, [filter, userData?.token]);

    // --- Select All logic ---
    const allIds = list.map(item => item.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
    const someSelected = allIds.some(id => selectedIds.includes(id)) && !allSelected;

    const handleSelectAll = (e) => {
        setSelectedIds(e.target.checked ? allIds : []);
    };

    const handleSelectRow = (id, checked) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
    };

    // --- Bulk Mark as Read ---
    const handleBulkMarkAsRead = async () => {
        if (selectedIds.length === 0) {
            message.warning('Please select at least one alert.');
            return;
        }

        setMarkingRead(true);
        try {
            const payload = allSelected ? { mark_all: true } : { ids: selectedIds };
            const response = await MakeApiCall(
                'mark-as-read-alerts-bulk',
                'post',
                payload,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                message.success('Selected alerts marked as read.');
                if (!isMounted.current) return;
                const markedCount = list.filter(item => selectedIds.includes(item.id) && !item.is_read).length;
                setUnreadCount(prev => Math.max(0, prev - markedCount));
                await getProductList(filter);
                // Refresh analytics with current filter after bulk action
                if (isMounted.current) fetchAnalyticsData(filter);
            } else {
                if (!isMounted.current) return;
                message.error(response?.message || 'Failed to mark alerts as read.');
            }
        } catch (error) {
            message.error('Something went wrong. Please try again.');
        } finally {
            if (isMounted.current) setMarkingRead(false);
        }
    };

    const handleAlertTypeFilter = (alertType) => {
        setFilter(prevFilter => ({
            ...prevFilter,
            alert_name: alertType,
            page: 1,
        }));
    };

    const columns = [
        {
            title: (
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                />
            ),
            key: 'select',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    checked={selectedIds.includes(record.id)}
                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                />
            ),
        },
        {
            title: 'Product Details',
            align: 'center',
            dataIndex: 'productName',
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.item_image || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a href="#" className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px">
                            {record?.item_name || ''}
                        </a>
                        <div className="fs-7 mt-1">
                            <span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span>
                        </div>
                        <div className="fs-7 mt-1">
                            <span><b>{record?.currency} {record?.item_price}</b></span>
                        </div>
                    </div>
                </div>
            ),
            sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            align: 'center',
            width: 100,
            sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
        },
        {
            title: 'Alert Name',
            dataIndex: 'alert_name',
            align: 'center',
            sorter: (a, b) => (a.alert_name || '').localeCompare(b.alert_name || ''),
        },
        {
            title: 'Description',
            align: 'center',
            dataIndex: 'description',
            render: (text) => (
                <div>
                    <Tooltip placement="top" title={text}>
                        <span className="text-two">{text}</span>
                    </Tooltip>
                </div>
            ),
            sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            align: 'center',
            render: (comment) => {
                if (comment) {
                    return <span>{comment}</span>;
                }
                return (
                    <a
                        onClick={() =>
                            Modal.info({
                                title: 'Add Comment',
                                content: 'Comment modal content here...',
                            })
                        }
                        style={{ color: 'red', fontWeight: 600 }}
                    >
                        Add Comment
                    </a>
                );
            },
            sorter: (a, b) => (a.comment || '').localeCompare(b.comment || ''),
        },
        {
            title: 'Alert Date & Time',
            dataIndex: 'alert_time',
            align: 'center',
            render: (text) => (
                text
                    ? <span>{moment.unix(text).format('YYYY-MM-DD HH:mm:ss')}</span>
                    : <span>-</span>
            ),
            sorter: (a, b) => a.alert_time - b.alert_time,
        },
        {
            title: 'Marketplace',
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {record.flag && <img src={record.flag} alt="" width={20} />}
                    <span style={{ fontWeight: 600 }}>{record.marketplace || '-'}</span>
                </div>
            ),
            sorter: (a, b) => (a.marketplace || '').localeCompare(b.marketplace || ''),
        },
        {
            title: 'Severity',
            dataIndex: 'severity',
            align: 'center',
            render: (severity) => severity ? (
                <Tag color={severity === 'Major' ? 'volcano' : 'green'}>{severity}</Tag>
            ) : '-',
            sorter: (a, b) => (a.severity || '').localeCompare(b.severity || ''),
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, row) => (
                <Dropdown overlay={menu(row)} trigger={['click']}>
                    <Button
                        type="link"
                        data-kt-menu-trigger="click"
                        data-kt-menu-placement="bottom-end"
                        data-kt-menu-overflow="true"
                    >
                        <i className="ki-solid ki-dots-vertical fs-1 text-gray-800"></i>
                    </Button>
                </Dropdown>
            ),
        },
    ];

    const menu = (row) => (
        <Menu className="min-w-200px p-3">
            <Menu.Item key="1" className="d-flex align-items-center mb-2">
                <i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Notification
            </Menu.Item>
            <Menu.Item key="2" className="d-flex align-items-center">
                <i className="ki-outline ki-trash fs-3 me-2"></i>Delete Notification
            </Menu.Item>
        </Menu>
    );

    const theme = {
        token: {
            itemActiveBg: '#ffffff',
            itemActiveBgDisabled: 'rgba(0, 0, 0, 0.15)',
            itemActiveColorDisabled: 'rgba(0, 0, 0, 0.25)',
            itemBg: '#ffffff',
            itemInputBg: '#ffffff',
            itemLinkBg: '#ffffff',
            itemSize: 32,
            itemSizeSM: 24,
            miniOptionsSizeChangerTop: 0,
            colorBgContainer: '#ffffff',
            colorBgContainerDisabled: 'rgba(0, 0, 0, 0.04)',
            colorBgTextActive: 'rgb(255, 255, 255)',
            colorBgTextHover: 'rgb(255, 255, 255)',
            colorBorder: '#d9d9d9',
            colorPrimary: '#f45b68',
            colorPrimaryBorder: '#f45b68',
            colorPrimaryHover: '#f45b68',
            colorText: 'rgba(0, 0, 0, 0.88)',
            colorTextDisabled: 'rgba(0, 0, 0, 0.25)',
            colorTextPlaceholder: 'rgba(0, 0, 0, 0.25)',
        },
    };

    return (
        <Wrapper>
            <div>
                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Alerts</h3>
                                <div className="card-toolbar">
                                    <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                                        <li className="nav-item">
                                            <Link
                                                className={`nav-link ${activeTab === 'category' ? 'active' : ''}`}
                                                data-bs-toggle="tab"
                                                onClick={() => setActiveTab('category')}
                                            >
                                                Category
                                            </Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link
                                                className={`nav-link ${activeTab === 'type' ? 'active' : ''}`}
                                                data-bs-toggle="tab"
                                                onClick={() => setActiveTab('type')}
                                            >
                                                Type
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card-body pb-2" style={{ minHeight: 300 }}>
                                {activeTab === 'category' && (
                                    <Category
                                        userData={userData}
                                        analyticsData={analyticsData}
                                        loading={analyticsLoading}
                                        activeCategory={selectedCategory}
                                        onCategorySelect={onCategoryChange}
                                    />
                                )}

                                {activeTab === 'type' && (
                                    <Type
                                        analyticsData={analyticsData}
                                        loading={analyticsLoading}
                                        onFilterChange={handleAlertTypeFilter}
                                        activeFilter={filter.alert_name}
                                    />
                                )}

                                <div className="tab-content" id="myTabContent">
                                    <div className="tab-pane fade show active" id="kt_tab_pane_1a" role="tabpanel"></div>
                                    <div className="tab-pane fade" id="kt_tab_pane_2a" role="tabpanel"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Alerts List</h3>
                                <div className="card-toolbar d-flex align-items-center gap-2">
                                    {selectedIds.length > 0 && (
                                        <button
                                            className="btn btn-danger fs-7 fw-bold"
                                            onClick={handleBulkMarkAsRead}
                                            disabled={markingRead}
                                        >
                                            {markingRead
                                                ? 'Marking...'
                                                : `Mark as Read${allSelected ? ' (All Selected)' : selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`
                                            }
                                        </button>
                                    )}
                                    <Select
                                        placeholder="Alert Status"
                                        style={{ width: 150, height: 35 }}
                                        value={filter.is_read !== null && filter.is_read !== undefined ? String(filter.is_read) : null}
                                        onChange={(val) => {
                                            const nextValue = (val === undefined || val === null) ? null : String(val);
                                            setFilter(prev => ({
                                                ...prev,
                                                is_read: nextValue,
                                                page: 1,
                                            }));
                                        }}
                                        className="me-2"
                                        allowClear
                                        onClear={() => setFilter(prev => ({ ...prev, is_read: null, page: 1 }))}
                                    >
                                        <Select.Option value="0">Unread</Select.Option>
                                        <Select.Option value="1">Read</Select.Option>
                                    </Select>
                                </div>
                            </div>
                            <div className="card-body py-2">
                                <div className="table-responsive">
                                    <Table
                                        scroll={{ x: 'max-content' }}
                                        columns={columns}
                                        loading={loading}
                                        dataSource={list}
                                        rowKey="id"
                                        pagination={false}
                                        rowClassName={(record) => record.is_read ? READ_ROW_CLASS : ''}
                                        components={{
                                            table: (props) => (
                                                <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                                            ),
                                            header: {
                                                wrapper: (props) => <thead {...props} className="" />,
                                                row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                                                cell: (props) => <th {...props} className="" />,
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={theme}>
                                    <Pagination
                                        className="mt-3"
                                        current={filter.page}
                                        pageSize={filter['per-page']}
                                        total={totalPage}
                                        showSizeChanger
                                        onChange={(currentPage, newPageSize) => {
                                            setFilter(prevFilter => ({
                                                ...prevFilter,
                                                page: currentPage,
                                                'per-page': newPageSize,
                                            }));
                                        }}
                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
}

export default AllAlerts;